import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { USER_ROLES } from '../../../utils/constants';
import {
  Target, Eye, Edit, Trash2, Plus,
  XCircle, CheckCircle, AlertTriangle, Calendar,
  User, TrendingUp, BarChart3, MoreVertical, ChevronDown, Check, Search, X,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import targetService from '../../../api/crm/targets/targetService';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';

import { API_ENDPOINTS } from '../../../api/config';
import {
  handleTextInputChange,
  handleNumberInputChange,
  handleSearchInputChange,
  validateText,
  validateNumberInput,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { capitalizeFirst } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import BulkTargetUpload from './BulkTargetUpload';

const CreatedTargets = ({
  targets: propTargets,
  setTargets: propSetTargets,
  onExport
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userRole } = usePermissions();
  const { user } = useAuth();

  // Initial filter/pagination/sort values restored from the URL so they survive refresh
  const initialSearch = searchParams.get('search') || '';
  const initialPeriod = searchParams.get('period') || 'all';
  const initialAssignee = searchParams.get('assignee') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = parseInt(searchParams.get('page'), 10) || 1;
  const initialLimit = parseInt(searchParams.get('limit'), 10) || 10;
  const initialSortBy = searchParams.get('sortBy') || 'createdAt';
  const initialSortOrder = searchParams.get('sortOrder') || 'desc';

  const isSalesExecutive = user?.role === 'sales_executive' || user?.role === 'export_executive' || user?.role === 'business_relationship_manager' || user?.role === 'business_development_executive';
  const isManager = user?.role === 'sales_manager' || user?.role === 'export_manager';

  const currentUserId =
    user?.id ||
    user?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return user?._id;
      }
    })();

  // Internal state for targets if not provided as prop
  const [internalTargets, setInternalTargets] = useState([]);

  // Use prop targets if provided, otherwise use internal state
  const targets = propTargets !== undefined ? propTargets : internalTargets;
  const setTargets = propSetTargets || setInternalTargets;

  // States
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [filterPeriod, setFilterPeriod] = useState(initialPeriod);
  const [customRange, setCustomRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [totalTargets, setTotalTargets] = useState(0);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortOrder);

  // Modal States (View modal removed - now using dedicated page)
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());


  // Edit Form State
  const [editForm, setEditForm] = useState({
    name: '',
    type: 'revenue',
    period: 'monthly',
    scope: 'individual',
    value: '',
    assignee: '',
    assigneeName: '',
    assignees: [],
    assigneeNames: [],
    team: '',
    region: '',
    status: 'active',
    baselineRules: ''
  });
  const [errors, setErrors] = useState({});

  // Assignee Filter States
  const [selectedAssigneeFilter, setSelectedAssigneeFilter] = useState(initialAssignee);
  const [isAssigneeFilterDropdownOpen, setIsAssigneeFilterDropdownOpen] = useState(false);
  const [assigneeFilterSearchTerm, setAssigneeFilterSearchTerm] = useState('');
  const [debouncedAssigneeFilterSearchTerm, setDebouncedAssigneeFilterSearchTerm] = useState('');
  const [assigneeFilterDropdownPosition, setAssigneeFilterDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeFilterDropdownRef = useRef(null);
  const assigneeFilterInputRef = useRef(null);

  // Debounce assignee filter search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAssigneeFilterSearchTerm(assigneeFilterSearchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeFilterSearchTerm]);

  // CRM Users for Assignee dropdown
  const [teamMembers, setTeamMembers] = useState([]);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);
  const MAX_VISIBLE_USERS = 20;
  const assigneeDropdownRef = useRef(null);
  const assigneeInputRef = useRef(null);

  const targetTypes = [
    { id: 'revenue', name: 'Revenue Target', description: 'Total sales revenue target' },
    { id: 'deals', name: 'Deal Count', description: 'Number of deals closed' },
    { id: 'newOpps', name: 'New Opportunities', description: 'New opportunities created' },
    { id: 'activities', name: 'Activities', description: 'Sales activities completed' }
  ];

  // Load targets from API with pagination
  const loadTargets = useCallback(async () => {
    try {
      setLoading(true);

      const isCustom = Boolean(customRange.startDate && customRange.endDate);

      const filters = {
        period: filterPeriod !== 'all' ? filterPeriod : undefined,
        search: searchQuery.trim() || undefined,
        range: isCustom ? 'custom' : 'all_time',
        startDate: isCustom ? customRange.startDate : undefined,
        endDate: isCustom ? customRange.endDate : undefined,
        page: currentPage,
        limit: itemsPerPage,
        assignee: selectedAssigneeFilter !== 'all' ? selectedAssigneeFilter : undefined
      };

      const result = await targetService.getAllTargets(filters);

      if (result.success) {
        const targetsData = Array.isArray(result.targets) ? result.targets : [];
        if (setTargets) {
          setTargets(targetsData);
        }
        if (result.total !== undefined) {
          setTotalTargets(result.total);
        } else {
          setTotalTargets(targetsData.length);
        }
      } else {
        if (setTargets) {
          setTargets([]);
        }
        setTotalTargets(0);
      }
    } catch (error) {
      if (setTargets) {
        setTargets([]);
      }
      setTotalTargets(0);
    } finally {
      setLoading(false);
    }
  }, [customRange, filterPeriod, searchQuery, currentPage, itemsPerPage, selectedAssigneeFilter, setTargets]);


  // React Query: Fetch CRM users for assignee filter with backend search
  const {
    data: assigneeFilterUsersData,
    isLoading: loadingAssigneeUsers,
    isFetching: isFetchingAssigneeUsers,
  } = useQuery({
    queryKey: [
      'crmAssigneeUsers',
      isManager ? (currentUserId || 'manager') : 'all',
      debouncedAssigneeFilterSearchTerm,
    ],
    queryFn: async () => {
      const params = {
        roleFilter: 'sales',
        limit: 100,
        ...(debouncedAssigneeFilterSearchTerm ? { search: debouncedAssigneeFilterSearchTerm } : {}),
      };

      if (isManager) {
        params.managerId = currentUserId;
      }

      const result = await teamManagementService.getCRMUsers(params);
      return result?.success && result.users ? result.users : [];
    },
    enabled: !isSalesExecutive && (isManager ? !!currentUserId : true),
    staleTime: 0,
  });

  const loadingUsers = loadingAssigneeUsers || isFetchingAssigneeUsers;

  // Sync teamMembers for modal from full CRM users list
  useEffect(() => {
    if (!debouncedAssigneeFilterSearchTerm && assigneeFilterUsersData) {
      const transformedUsers = assigneeFilterUsersData.map(u => ({
        id: u.user || u._id || u.id,
        name: u.name || 'Unknown',
        email: u.email || '',
        role: u.role || '',
        department: u.department || '',
        designation: u.designation || '',
        team: u.department || 'Sales Team'
      }));

      if (currentUserId) {
        const selfExists = transformedUsers.some(u => u.id === currentUserId);
        if (!selfExists) {
          transformedUsers.push({
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            role: user?.role || '',
            department: user?.department || '',
            designation: user?.designation || '',
            team: user?.department || 'Sales Team'
          });
        }
      }

      transformedUsers.sort((a, b) => a.name.localeCompare(b.name));
      setTeamMembers(transformedUsers);
      setFilteredTeamMembers(transformedUsers);
    }
  }, [assigneeFilterUsersData, debouncedAssigneeFilterSearchTerm, currentUserId, user]);

  // Filter team members based on search term
  useEffect(() => {
    if (assigneeSearchTerm.trim() === '') {
      setFilteredTeamMembers(teamMembers);
    } else {
      const searchLower = assigneeSearchTerm.toLowerCase().trim();
      const filtered = teamMembers.filter(member =>
        member.name.toLowerCase().includes(searchLower) ||
        (member.email && member.email.toLowerCase().includes(searchLower)) ||
        (member.role && member.role.toLowerCase().includes(searchLower)) ||
        (member.department && member.department.toLowerCase().includes(searchLower))
      );
      setFilteredTeamMembers(filtered);
    }
  }, [assigneeSearchTerm, teamMembers]);

  // Derive filtered assignee filter options based on fetched data and search term
  const filteredAssigneeFilterOptions = useMemo(() => {
    if (!assigneeFilterUsersData) return [];

    const lowerTerm = debouncedAssigneeFilterSearchTerm.toLowerCase();

    const mappedUsers = assigneeFilterUsersData.map(u => ({
      id: u.user || u._id || u.id,
      name: u.name || 'Unknown',
      email: u.email || '',
      role: u.role || '',
      department: u.department || '',
      designation: u.designation || '',
      team: u.department || 'Sales Team'
    }));

    if (currentUserId && isManager) {
      const selfExists = mappedUsers.some(u => u.id === currentUserId);
      if (!selfExists) {
        const selfMatches = !debouncedAssigneeFilterSearchTerm ||
          `${user?.firstName || ''} ${user?.lastName || ''}`.toLowerCase().includes(lowerTerm) ||
          (user?.email && user.email.toLowerCase().includes(lowerTerm));
        if (selfMatches) {
          mappedUsers.push({
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            role: user?.role || '',
            department: user?.department || '',
            designation: user?.designation || '',
            team: user?.department || 'Sales Team'
          });
        }
      }
    }

    mappedUsers.sort((a, b) => a.name.localeCompare(b.name));

    const prefixOptions = [];
    if (!debouncedAssigneeFilterSearchTerm || 'all assignees'.includes(lowerTerm)) {
      prefixOptions.push({ id: 'all', name: 'All Assignees', role: 'system' });
    }
    if (!isSalesExecutive) {
      if (isManager && (!debouncedAssigneeFilterSearchTerm || 'my team'.includes(lowerTerm))) {
        prefixOptions.push({ id: 'my_team', name: 'My Team', role: 'system' });
      }
      if (!debouncedAssigneeFilterSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }
    }

    return [...prefixOptions, ...mappedUsers];
  }, [assigneeFilterUsersData, debouncedAssigneeFilterSearchTerm, isSalesExecutive, isManager, currentUserId, user]);

  // Maintain assigneeFilterOptions cache for button display label
  const [assigneeFilterOptions, setAssigneeFilterOptions] = useState([
    { id: 'all', name: 'All Assignees', role: 'system' },
    { id: 'my_team', name: 'My Team', role: 'system' },
    { id: 'unassigned', name: 'Unassigned', role: 'system' },
  ]);

  useEffect(() => {
    if (filteredAssigneeFilterOptions.length > 0) {
      setAssigneeFilterOptions(prev => {
        const map = new Map();
        prev.forEach(item => { if (item && item.id) map.set(item.id, item); });
        filteredAssigneeFilterOptions.forEach(item => { if (item && item.id) map.set(item.id, item); });
        if (map.size === prev.length) return prev;
        return Array.from(map.values());
      });
    }
  }, [filteredAssigneeFilterOptions]);

  // Position calculation for assignee filter dropdown
  useEffect(() => {
    if (isAssigneeFilterDropdownOpen && assigneeFilterDropdownRef.current) {
      const rect = assigneeFilterDropdownRef.current.getBoundingClientRect();
      setAssigneeFilterDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isAssigneeFilterDropdownOpen]);

  // Close assignee filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeFilterDropdownRef.current && !assigneeFilterDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setIsAssigneeFilterDropdownOpen(false);
          setAssigneeFilterSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setIsAssigneeDropdownOpen(false);
        setAssigneeSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted targets
  const getSortedTargets = () => {
    if (!sortColumn || !targets || targets.length === 0) return targets;

    return [...targets].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortColumn === 'type') {
        aValue = (a.type || '').toLowerCase();
        bValue = (b.type || '').toLowerCase();
      } else if (sortColumn === 'period') {
        aValue = (a.period || '').toLowerCase();
        bValue = (b.period || '').toLowerCase();
      } else if (sortColumn === 'scope') {
        aValue = (a.scope || '').toLowerCase();
        bValue = (b.scope || '').toLowerCase();
      } else if (sortColumn === 'assignee') {
        aValue = (a.assigneeName || '').toLowerCase();
        bValue = (b.assigneeName || '').toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (sortColumn === 'startDate') {
        aValue = new Date(a.startDate || 0).getTime();
        bValue = new Date(b.startDate || 0).getTime();
      } else if (sortColumn === 'status') {
        aValue = (a.computedStatus || a.status || '').toLowerCase();
        bValue = (b.computedStatus || b.status || '').toLowerCase();
      } else if (sortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Load targets on mount, when location changes, or when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTargets();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, filterPeriod, selectedAssigneeFilter, currentPage, itemsPerPage, customRange.startDate, customRange.endDate, loadTargets]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Reset to page 1 when filters or items per page changes
  const isInitialPageResetMount = useRef(true);
  useEffect(() => {
    if (isInitialPageResetMount.current) {
      isInitialPageResetMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchQuery, filterPeriod, selectedAssigneeFilter, customRange.startDate, customRange.endDate, itemsPerPage]);

  // Persist filters/pagination/sort to URL
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'period', 'assignee', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (filterPeriod && filterPeriod !== 'all') params.set('period', filterPeriod);
      if (selectedAssigneeFilter && selectedAssigneeFilter !== 'all') params.set('assignee', selectedAssigneeFilter);
      if (customRange.startDate) params.set('startDate', customRange.startDate);
      if (customRange.endDate) params.set('endDate', customRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'createdAt') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    searchQuery,
    filterPeriod,
    selectedAssigneeFilter,
    customRange.startDate,
    customRange.endDate,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);


  // Format date helper function
  const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

      if (options.includeTime) {
        const timePart = date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        return `${datePart}, ${timePart}`;
      }

      return datePart;
    } catch (error) {
      return 'N/A';
    }
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage, itemsPerPage, searchQuery, filterPeriod, selectedAssigneeFilter]);


  const handleSelectAll = (e) => {
    const data = getSortedTargets();
    if (e.target.checked) {
      const allIds = data.map(item => item.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'targets',
            format,
            scope,
            filters: overrideFilters ?? {
              period: filterPeriod !== 'all' ? filterPeriod : undefined,
              search: searchQuery.trim() || undefined,
              range: (customRange.startDate && customRange.endDate) ? 'custom' : 'all_time',
              startDate: customRange.startDate || undefined,
              endDate: customRange.endDate || undefined,
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `targets-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [searchQuery, filterPeriod, customRange, currentPage, itemsPerPage, sortColumn, sortDirection]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleTargetsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.size) {
        alert('No selected targets found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleTargetsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            range: 'custom',
            startDate: meta.startDate,
            endDate: meta.endDate,
          };
        }

        await requestBackendExport(params);
        setShowExportModal(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport]
  );




  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalTargets / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalTargets);

  const handleViewTarget = (target) => {
    if (!target || !target.id) return;
    navigate(`/target/${target.id}`);
  };

  const handleEditTarget = (target) => {
    if (!target || !target.id) return;
    navigate(`/target_builder?id=${encodeURIComponent(target.id)}`);
  };

  const handleUpdateTarget = async (e) => {
    e.preventDefault();

    const isTeamScope = editForm.scope === 'team';

    const requiredFields = ['value'];
    const validation = validateRequiredFields(editForm, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    if (isTeamScope) {
      if (!editForm.assignees || editForm.assignees.length === 0) {
        setErrors({ assignee: 'Please select at least one team member' });
        return;
      }
    } else {
      if (!editForm.assignee) {
        setErrors({ assignee: 'Please select a valid assignee' });
        return;
      }
    }

    const valueValidation = validateNumberInput(editForm.value, Infinity, 0);
    if (!valueValidation.isValid) {
      setErrors({ ...errors, value: valueValidation.error });
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      const primaryAssigneeId = isTeamScope
        ? (editForm.assignees && editForm.assignees.length > 0 ? editForm.assignees[0] : '')
        : editForm.assignee;

      const selectedIds = isTeamScope ? (editForm.assignees || []) : [primaryAssigneeId];
      const selectedMembers = teamMembers.filter(m => selectedIds.includes(m.id));

      const updateData = {
        name: editForm.name || `${editForm.type} - ${editForm.period}`,
        type: editForm.type,
        period: editForm.period,
        scope: editForm.scope,
        value: parseFloat(editForm.value),
        assignee: primaryAssigneeId,
        assignees: isTeamScope ? selectedIds : undefined,
        team: selectedMembers[0]?.department || selectedMembers[0]?.team || editForm.team,
        region: editForm.region,
        status: editForm.status,
        baselineRules: editForm.baselineRules
      };

      const result = await targetService.updateTarget(
        editingTarget.id,
        updateData
      );

      if (result.success) {
        if (setTargets) {
          setTargets(prev => prev.map(t =>
            t.id === editingTarget.id ? result.target : t
          ));
        }

        setShowEditModal(false);
        setEditingTarget(null);
        alert('Target updated successfully');
        loadTargets();
      } else {
        alert('Failed to update target: ' + result.error);
      }
    } catch (error) {
      alert('Error updating target. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTarget = (targetId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTargetToDelete(targetId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTarget = async () => {
    if (!targetToDelete) return;

    try {
      setLoading(true);
      const result = await targetService.deleteTarget(targetToDelete);

      if (result.success) {
        if (setTargets) {
          setTargets(prev => prev.filter(t => t.id !== targetToDelete));
        }
        alert('Target deleted successfully');
        loadTargets();
      } else {
        alert('Failed to delete target: ' + result.error);
      }
    } catch (error) {
      alert('Error deleting target. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setTargetToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setLoading(true);
      const result = await targetService.bulkDeleteTargets(Array.from(selectedRows));

      if (result.success) {
        alert('Targets deleted successfully');
        setSelectedRows(new Set());
        loadTargets();
      } else {
        alert('Failed to delete targets: ' + result.error);
      }
    } catch (error) {
      alert('Error during bulk deletion. Please try again.');
    } finally {
      setLoading(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTargetToDelete(null);
  };




  // Get Status Color
  const getStatusColor = (status, computedStatus) => {
    const effectiveStatus = (computedStatus || 'draft').toLowerCase();
    switch (effectiveStatus) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'incomplete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status label
  const formatStatusLabel = (status, computedStatus) => {
    const safeStatus = computedStatus || 'draft';
    return capitalizeFirst(safeStatus.toString());
  };

  // Format target name
  const formatTargetName = (target) => {
    const name = target.name || `${target.type} - ${target.period}`;
    return name.replace(/\b\w/g, char => char.toUpperCase());
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'revenue':
        return 'bg-blue-100 text-blue-800';
      case 'deals':
        return 'bg-purple-100 text-purple-800';
      case 'newOpps':
        return 'bg-green-100 text-green-800';
      case 'activities':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const displayTargets = targets || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Target Management</h2>
          <p className="text-sm text-gray-600">Create, manage, and track all your sales targets and their performance</p>
        </div>

        <div className="flex items-center space-x-2">
          {selectedRows.size > 0 && (
            <PermissionGate permission="target_achievement" action="delete">
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedRows.size})
              </button>
            </PermissionGate>
          )}

          <PermissionGate permission="target_achievement" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <button
                onClick={handleExportClick}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ml-2 ${
                  selectedRows.size > 0
                    ? 'bg-blue-50 border-2 border-blue-200 text-blue-600'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
                disabled={exportLoading || (displayTargets.length === 0 && selectedRows.size === 0)}
              >
                <Download className="h-4 w-4 mr-2" />
                {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
              </button>
              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                onExport={handleTargetsSelectedFormatExport}
                loading={exportLoading}
                formats={['csv', 'xlsx', 'pdf']}
              />
            </div>
          </PermissionGate>


          {!isSalesExecutive && (
            <PermissionGate permission="target_achievement" action="create">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4 rotate-180" />
                <span>Bulk Upload</span>
              </button>
            </PermissionGate>
          )}

          {!isSalesExecutive && (
            <PermissionGate permission="target_achievement" action="create">
              <button
                onClick={() => navigate('/target_builder')}
                className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Create Target</span>
              </button>
            </PermissionGate>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by target name, assignee, team..."
              value={searchQuery}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(e, setSearchQuery, FIELD_LIMITS.search);
                setSearchQuery(sanitized);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
              maxLength={FIELD_LIMITS.search}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center lg:flex-shrink-0">
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Plan Periods</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>

            {!isSalesExecutive && (
              <div className="relative">
                <button
                  type="button"
                  ref={assigneeFilterDropdownRef}
                  onClick={() => {
                    const isOpening = !isAssigneeFilterDropdownOpen;
                    setIsAssigneeFilterDropdownOpen(isOpening);
                    if (isOpening) {
                      setAssigneeFilterSearchTerm('');
                      setTimeout(() => {
                        assigneeFilterInputRef.current?.focus();
                      }, 100);
                    }
                  }}
                  disabled={loadingUsers}
                  className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm min-w-[160px] w-full sm:w-auto"
                >
                  <span className="truncate whitespace-nowrap mr-2">
                    {selectedAssigneeFilter === 'all'
                      ? 'All Assignees'
                      : selectedAssigneeFilter === 'my_team'
                        ? 'My Team'
                        : selectedAssigneeFilter === 'unassigned'
                          ? 'Unassigned'
                          : assigneeFilterOptions.find(opt => opt.id === selectedAssigneeFilter)?.name || 'All Assignees'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isAssigneeFilterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isAssigneeFilterDropdownOpen && (
                  <div
                    data-dropdown-assignee-filter
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeFilterDropdownPosition.top}px`,
                      left: `${assigneeFilterDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeFilterDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          ref={assigneeFilterInputRef}
                          type="text"
                          value={assigneeFilterSearchTerm}
                          onChange={(e) => setAssigneeFilterSearchTerm(e.target.value)}
                          placeholder="Search assignees..."
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingUsers && filteredAssigneeFilterOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                      ) : filteredAssigneeFilterOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">No assignees found</div>
                      ) : (
                        <div className="flex flex-col">
                          {filteredAssigneeFilterOptions.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setAssigneeFilterOptions(prev => prev.some(o => o.id === option.id) ? prev : [...prev, option]);
                                setSelectedAssigneeFilter(option.id);
                                setIsAssigneeFilterDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${selectedAssigneeFilter === option.id ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                              <span className="font-medium text-gray-900">{option.name}</span>
                              {option.email && (
                                <span className="text-xs text-gray-500 truncate">{option.email}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DateRangeFilter
              startDate={customRange.startDate}
              endDate={customRange.endDate}
              onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
              allowFutureDates={false}
            />

            {(customRange.startDate || customRange.endDate) && (
              <Button
                variant="outline"
                onClick={() => {
                  setCustomRange({ startDate: '', endDate: '' });
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from({ length: 11 }).map((_, index) => (
                    <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 5 }).map((_, rowIndex) => (
                  <tr key={rowIndex} className="animate-pulse">
                    {Array.from({ length: 11 }).map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : displayTargets.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={getSortedTargets().length > 0 && getSortedTargets().every(item => selectedRows.has(item.id))}
                      onChange={(e) => {
                        const currentData = getSortedTargets();
                        if (e.target.checked) {
                          const newSelected = new Set(selectedRows);
                          currentData.forEach(item => newSelected.add(item.id));
                          setSelectedRows(newSelected);
                        } else {
                          const newSelected = new Set(selectedRows);
                          currentData.forEach(item => newSelected.delete(item.id));
                          setSelectedRows(newSelected);
                        }
                      }}
                      disabled={getSortedTargets().length === 0}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Target Name</span>
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {sortColumn === 'type' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('period')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Period</span>
                      {sortColumn === 'period' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('scope')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Scope</span>
                      {sortColumn === 'scope' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('assignee')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                      {sortColumn === 'assignee' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'value' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('startDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Period Range</span>
                      {sortColumn === 'startDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortColumn === 'createdAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedTargets().map((target) => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={selectedRows.has(target.id)}
                        onChange={() => handleSelectRow(target.id)}
                      />
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div
                        className="text-sm font-medium text-gray-900 truncate"
                        title={formatTargetName(target)}
                      >
                        {formatTargetName(target)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(target.type)}`}>
                        {targetTypes.find(t => t.id === target.type)?.name || target.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                      {target.period}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                      {target.scope}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {(() => {
                        let list = [];
                        if (target.assigneesInfo && target.assigneesInfo.length > 0) {
                          list = target.assigneesInfo.map(m => ({
                            name: m.name || 'Unknown',
                            email: m.email || ''
                          }));
                        } else if (target.assigneeNames && target.assigneeNames.length > 0) {
                          list = target.assigneeNames.map((n, idx) => ({
                            name: n || 'Unknown',
                            email: ''
                          }));
                        } else if (target.assigneeInfo) {
                          list = [{
                            name: target.assigneeInfo.name || target.assigneeName || 'Unknown',
                            email: target.assigneeInfo.email || ''
                          }];
                        } else {
                          list = [{
                            name: target.assigneeName || 'Unknown',
                            email: ''
                          }];
                        }

                        const hasMultiple = list.length > 1;
                        if (hasMultiple) {
                          const firstAssignee = list[0];
                          return (
                            <div className="relative group inline-block">
                              <span className="flex items-start space-x-1 cursor-help hover:text-blue-600 transition-colors">
                                <span className="flex flex-col leading-tight">
                                  <span className="font-medium text-gray-900">{firstAssignee.name}</span>
                                  {firstAssignee.email && <span className="text-xs text-gray-500">{firstAssignee.email}</span>}
                                </span>
                                <span className="inline-flex items-center px-1.5 py-0.25 rounded-md text-xs font-bold bg-blue-50 text-blue-600 border border-blue-200 mt-0.5">
                                  +{list.length - 1}
                                </span>
                              </span>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[260px] transition-all duration-200">
                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1">
                                  Team Assignees ({list.length})
                                </div>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                  {list.map((member, idx) => (
                                    <div key={idx} className="flex flex-col text-xs leading-normal">
                                      <span className="font-semibold text-gray-800">{member.name}</span>
                                      {member.email && <span className="text-gray-500 font-normal">{member.email}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        } else {
                          const name = list[0]?.name || 'Unknown';
                          const email = list[0]?.email || '';
                          return (
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium text-gray-900">{name}</span>
                              {email && <span className="text-xs text-gray-500">{email}</span>}
                            </div>
                          );
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {target.type === 'revenue' || target.type === 'activities'
                        ? `₹${formatIndianAmount(target.value || 0)}`
                        : target.value?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {target.startDate && target.endDate
                        ? `${formatDate(target.startDate)} – ${formatDate(target.endDate)}`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(target.status, target.computedStatus)}`}>
                        {formatStatusLabel(target.status, target.computedStatus)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(target.createdAt, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <PermissionGate permission="target_achievement" action="read">
                          <button
                            onClick={() => handleViewTarget(target)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="target_achievement" action="update">
                          <button
                            onClick={() => handleEditTarget(target)}
                            className={`${(target.computedStatus && target.computedStatus.toLowerCase() === 'completed')
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-green-600 hover:text-green-900'
                              }`}
                            disabled={target.computedStatus && target.computedStatus.toLowerCase() === 'completed'}
                            title="Edit Target"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="target_achievement" action="delete">
                          <button
                            type="button"
                            onClick={(e) => handleDeleteTarget(target.id, e)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Target"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Targets Found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || filterPeriod !== 'all' || selectedAssigneeFilter !== 'all' || customRange.startDate || customRange.endDate
              ? 'No targets match your search criteria'
              : 'Create your first target to start tracking performance'}
          </p>
          {!(searchQuery || filterPeriod !== 'all' || selectedAssigneeFilter !== 'all' || customRange.startDate || customRange.endDate) && !isSalesExecutive && (
            <PermissionGate permission="target_achievement" action="create">
              <button
                onClick={() => navigate('/target_builder')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Create Target
              </button>
            </PermissionGate>
          )}
        </div>
      )}

      {(() => {
        const shouldShowPagination = !loading && totalPages > 1;

        return shouldShowPagination ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {endIndex} of {totalTargets} results
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value={3}>3</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];

                    if (totalPages <= 10) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);

                      if (currentPage <= 4) {
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push('ellipsis');
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                            ? 'bg-agrosaf-green text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    });
                  })()}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {showEditModal && editingTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Target</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTarget(null);
                    setErrors({});
                    setIsAssigneeDropdownOpen(false);
                    setAssigneeSearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form className="p-5 space-y-4" onSubmit={handleUpdateTarget}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Target Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => {
                      handleTextInputChange(e, (value) => {
                        const formatted = capitalizeFirst(value);
                        setEditForm(prev => ({ ...prev, name: formatted }));
                      }, FIELD_LIMITS.customerName);
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: '' }));
                      }
                    }}
                    className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter target name"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(editForm.name, FIELD_LIMITS.customerName)}
                    </span>
                    {errors.name && (
                      <span className="text-red-500 text-xs">{errors.name}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Target Type
                  </label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {targetTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Period
                  </label>
                  <select
                    value={editForm.period}
                    onChange={(e) => setEditForm(prev => ({ ...prev, period: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Scope
                  </label>
                  <select
                    value={editForm.scope}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      scope: e.target.value,
                      assignee: '',
                      assignees: []
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="individual">Individual</option>
                    <option value="team">Team</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Target Value *
                  </label>
                  <input
                    type="number"
                    value={editForm.value}
                    onChange={(e) => {
                      handleNumberInputChange(e, (value) => {
                        setEditForm(prev => ({ ...prev, value: value }));
                      }, Infinity, 0);
                      if (errors.value) {
                        setErrors(prev => ({ ...prev, value: '' }));
                      }
                    }}
                    className={`w-full border ${errors.value ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter target value"
                    required
                  />
                  {errors.value && (
                    <p className="text-red-500 text-xs mt-1">{errors.value}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="relative" ref={assigneeDropdownRef}>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    {editForm.scope === 'team' ? 'Assignees *' : 'Assignee *'}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen);
                        if (!isAssigneeDropdownOpen) {
                          setTimeout(() => {
                            assigneeInputRef.current?.focus();
                          }, 100);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 border ${errors.assignee ? 'border-red-500' : 'border-gray-300'
                        } rounded-md bg-white flex items-center justify-between ${loadingUsers ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
                        } text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      disabled={loadingUsers}
                    >
                      <span className={(editForm.assignee || (editForm.assignees && editForm.assignees.length > 0)) ? 'text-gray-900' : 'text-gray-500'}>
                        {editForm.scope === 'team'
                          ? (editForm.assignees && editForm.assignees.length > 0
                            ? `${editForm.assignees.length} member(s) selected`
                            : (loadingUsers ? 'Loading users...' : 'Select team members...'))
                          : (editForm.assignee
                            ? teamMembers.find(m => m.id === editForm.assignee)?.name || 'Select assignee...'
                            : (loadingUsers ? 'Loading users...' : 'Select assignee...'))}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isAssigneeDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>

                    {isAssigneeDropdownOpen && (
                      <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              ref={assigneeInputRef}
                              type="text"
                              value={assigneeSearchTerm}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                setAssigneeSearchTerm(value);
                              }}
                              placeholder="Search users..."
                              maxLength={100}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-[240px]">
                          {loadingUsers && filteredTeamMembers.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              Loading CRM team members...
                            </div>
                          ) : filteredTeamMembers.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              {assigneeSearchTerm ? 'No users found' : 'No CRM team members available'}
                            </div>
                          ) : (
                            (assigneeSearchTerm.trim() === ''
                              ? filteredTeamMembers.slice(0, MAX_VISIBLE_USERS)
                              : filteredTeamMembers
                            ).map((member) => {
                              const isSelectedIndividual = editForm.assignee === member.id;
                              const isSelectedTeam = editForm.assignees?.includes(member.id);
                              const isSelected = editForm.scope === 'team' ? isSelectedTeam : isSelectedIndividual;

                              return (
                                <button
                                  key={member.id}
                                  type="button"
                                  onClick={() => {
                                    if (editForm.scope === 'team') {
                                      setEditForm(prev => {
                                        const current = prev.assignees || [];
                                        const exists = current.includes(member.id);
                                        const nextAssignees = exists
                                          ? current.filter(id => id !== member.id)
                                          : [...current, member.id];
                                        return { ...prev, assignees: nextAssignees };
                                      });
                                      if (errors.assignee) {
                                        setErrors(prev => ({ ...prev, assignee: '' }));
                                      }
                                    } else {
                                      setEditForm(prev => ({
                                        ...prev,
                                        assignee: member.id,
                                        assigneeName: member.name || '',
                                        team: member.department || member.team || '',
                                        region: member.region || 'North'
                                      }));
                                      setIsAssigneeDropdownOpen(false);
                                      setAssigneeSearchTerm('');
                                      if (errors.assignee) {
                                        setErrors(prev => ({ ...prev, assignee: '' }));
                                      }
                                    }
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-50 font-medium' : 'text-gray-700'
                                    }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium">{member.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {member.email} {member.designation ? `• ${member.designation}` : (member.department ? `• ${member.department}` : '')}
                                      </div>
                                    </div>
                                    {editForm.scope === 'team' ? (
                                      <input
                                        type="checkbox"
                                        readOnly
                                        checked={isSelectedTeam}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                      />
                                    ) : (
                                      isSelectedIndividual && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                      )
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.assignee && (
                    <p className="text-red-500 text-xs mt-1">{errors.assignee}</p>
                  )}
                  {!loadingUsers && teamMembers.length === 0 && !isAssigneeDropdownOpen && (
                    <p className="text-xs text-yellow-600 mt-1">No CRM team members found</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Team
                  </label>
                  <input
                    type="text"
                    value={editForm.team}
                    onChange={(e) => {
                      handleTextInputChange(e, (value) => {
                        // Auto-capitalize first letter of team name
                        const formatted = capitalizeFirst(value);
                        setEditForm(prev => ({ ...prev, team: formatted }));
                      }, FIELD_LIMITS.customerName);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter team name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Region
                  </label>
                  <select
                    value={editForm.region}
                    onChange={(e) => setEditForm(prev => ({ ...prev, region: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select region...</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Baseline Rules
                </label>
                <textarea
                  value={editForm.baselineRules}
                  onChange={(e) => {
                    handleTextInputChange(e, (value) => {
                      setEditForm(prev => ({ ...prev, baselineRules: value }));
                    }, FIELD_LIMITS.notes);
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter baseline calculation rules and carry-over policies..."
                ></textarea>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-500">
                    {getCharacterCount(editForm.baselineRules, FIELD_LIMITS.notes)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTarget(null);
                    setErrors({});
                    setIsAssigneeDropdownOpen(false);
                    setAssigneeSearchTerm('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {loading ? 'Updating...' : 'Update Target'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Target
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete this target? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteTarget}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Created Targets"
        showDateRange
        scopedFormatExport={{
          onExport: handleTargetsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />


      <ConfirmationDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedRows.size} selected target(s)? This action cannot be undone.`}
        confirmText="Delete All"
        type="danger"
      />

      <BulkTargetUpload
        isOpen={showBulkUpload}
        onComplete={() => {
          setShowBulkUpload(false);
          loadTargets();
        }}
        onCancel={() => setShowBulkUpload(false)}
      />
    </div>
  );
};

export default CreatedTargets;

